import React, { Component } from 'react'
// components
import LoginForm from '../LoginForm'
import Layout from '../Layout'
import LoadingPage from '../LoadingPage'
import ResetPasswordForm from '../ResetPasswordForm'
import SpoofWarning from '../SpoofWarning'
// styles
import './app.css'
import flashMessageStyles from '../../utilities/flashMessageStyles.js'
// internal utilities
import { createAuthApi } from '../../utilities/api.js'
import getLabel from '../../utilities/label.js'
// external utilities
import NotificationSystem from 'react-notification-system'
import _ from 'lodash'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'

export default class App extends Component {
  constructor(props) {
    super(props)
    const credentials = this.getCredentials()
    this.state = { credentials: credentials }
    this.notificationSystemRef = React.createRef()
    this.initializeApi = this.initializeApi.bind(this)
    this.logout = this.logout.bind(this)
    this.getCurrentUser = this.getCurrentUser.bind(this)
    this.getResource = this.getResource.bind(this)
    this.postResource = this.postResource.bind(this)
    this.postComment = this.postComment.bind(this)
    this.postResetPassword = this.postResetPassword.bind(this)
    this.postChangePassword = this.postChangePassword.bind(this)
    this.postMarkMessagesRead = this.postMarkMessagesRead.bind(this)
    this.changeEvent = this.changeEvent.bind(this)
    this.loadSurveyFromEmail = this.loadSurveyFromEmail.bind(this)
    this.refreshData = this.refreshData.bind(this)
    this.loadData = this.loadData.bind(this)
    this.flashMessage = this.flashMessage.bind(this)
  }

  componentDidMount() {
    if (this.state.credentials) {
      this.initializeApi()
      if (this.state.credentials.isSpoofing) {
        this.refreshData()
      } else {
        // Ensure we're able to connect to the api otherwise load existing data.
        this.api.get('/').then(() => {
          this.refreshData()
        }).catch(() => {
          this.loadData()
        })
      }
    }
    this.notificationSystem = this.notificationSystemRef.current
  }

  getCredentials() {
    const credentials = JSON.parse(localStorage.getItem('credentials'))
    const url = new URL(window.location.href)
    const email = url.searchParams.get('user')
    const authToken = url.searchParams.get('auth_token')
    if (email && authToken) {
      const credentialObj = {
        auth_token: authToken,
        email: email,
        isSpoofing: true
      }
      localStorage.setItem('credentials', JSON.stringify(credentialObj))
      return credentialObj
    } else if (credentials) {
      return credentials
    }
  }

  initializeApi() {
    const credentials = this.state.credentials
    const email = credentials.email || localStorage.getItem('email')
    window.api = this.api = createAuthApi({
      'Authorization': `Token token="${ credentials.auth_token }"`,
      'X_API_EMAIL': email,
    })
  }

  changeEvent(event) {
    // set the event from the api
    this.api.get('/app/set_event/' + event.id).then(() => {
      this.refreshData()
      window.location.href = '/homepage'
    }).catch(() => {
    })
  }

  loadSurveyFromEmail(url) {
    let eventId = url.split('/')[4]
    let eventSurveyId = url.split('/')[2]
    this.api.get('/app/set_event/' + eventId).then(() => {
      localStorage.setItem('event_survey_id', eventSurveyId)
      this.refreshData()
    }).catch(() => {
    })
  }

  refreshData() {
    localStorage.removeItem('users')
    localStorage.removeItem('messages')
    localStorage.removeItem('events')
    localStorage.removeItem('sessions')
    localStorage.removeItem('sponsors')
    localStorage.removeItem('leaderboard')
    localStorage.removeItem('homepage')
    localStorage.removeItem('logged_in_user')
    localStorage.removeItem('agenda_filters')
    localStorage.removeItem('user_filters')
    this.setState({
      users: null,
      messages: null,
      events: null,
      sessions: null,
      sponsors: null,
      leaderboard: null,
      homepage: null,
      logged_in_users: null,
    }, this.loadData)
  }

  loadData() {
    this.getCurrentUser({
      callback: () => {
        this.getResource({ key: 'users'})
        this.getResource({ key: 'messages'})
        this.getResource({ key: 'events'})
        this.getResource({ key: 'sessions'})
        this.getResource({ key: 'sponsors'})
        this.getResource({ key: 'leaderboard'})
        this.getResource({ key: 'homepage'})
      }
    })
  }

  getCurrentUser(options) {
    let { callback, update } = options
    callback = callback || (() => {})
    const currentUser = JSON.parse(localStorage.getItem('logged_in_user'))
    if (currentUser && !update) {
      this.setState({ currentUser: currentUser })
      window.currentUser = currentUser
      callback()
    } else {
      this.api.get('/app/navigations').then(
        (response) => {
          let user = response.data.app_user
          let key = this.getDefaultStartPageKey(user)
          window.history.pushState('', '', '/' + key)
          localStorage.setItem('logged_in_user', JSON.stringify(user))
          window.currentUser = user
          this.setState({ currentUser: user }, callback)
        }
      ).catch(
        (error) => {
          if (error?.response?.status === 401) {
            this.logout()
            return
          }
          this.escape({
            callback: () => this.getCurrentUser(options)
          })
        }
      )
    }
  }

  getResource(options) {
    let { key, update, callback } = options
    let { failedAttempt } = this.state
    let resource = localStorage.getItem(key)
    if (resource && !update) {
      this.setState({ [key]: JSON.parse(resource) })
    } else {
      this.api.get(this.getResourceUrl(key)).then(
        (response) => {
          localStorage.setItem(key, JSON.stringify(response.data))
          this.setState({ [key]: response.data })
          callback && callback()
        }
      ).catch(
        (e) => {
          if (e.response.status === 403 && !failedAttempt) {
            this.setState({ failedAttempt: true }, () => {
              this.getCurrentUser({
                update: true,
                callback: () => this.getResource(options)
              })
            })
          } else if (e.response.status === 401 && !failedAttempt) {
            this.logout()
          } else if (e.response.status === 503 && !failedAttempt) {
            confirmAlert({
              title: 'Down for Maintenance',
              message: 'The site is temporarily down for maintenance.\nPlease check back soon.',
              buttons: [
                {
                  label: getLabel('Continue'),
                  onClick: () => this.setState({ escapeError: false}, callback)
                },
                {
                  label: getLabel('Logout'),
                  onClick: this.logout
                }
              ]
            })
          } else {
            this.escape({
              callback: () => this.getResource(options)
            })
          }
        }
      )
    }
  }

  escape(options) {
    let { callback } = options
    callback = callback || (() => {})
    if (!this.state.escapeError) {
      this.setState({ escapeError: true}, () => {
        confirmAlert({
          title: getLabel('Error'),
          message: getLabel('You do not have a valid event. Please see support staff for assistance.'),
          buttons: [
            {
              label: getLabel('Try Again'),
              onClick: () => this.setState({ escapeError: false}, callback)
            },
            {
              label: getLabel('Logout'),
              onClick: this.logout
            }
          ]
        })
      })
    }
  }

  getResourceUrl(key) {
    var currentUser = this.state.currentUser || window.currentUser
    const eventID = currentUser.current_event_id
    let url = ''
    switch (key) {
    case 'users':
      url = '/app/users/' + eventID
      break
    case 'messages':
      url = '/app/messages/' + eventID
      break
    case 'events':
      url = '/app/current_user/events'
      break
    case 'sessions':
      url = '/app/sessions/' + eventID
      break
    case 'sponsors':
      url = '/app/sponsors/' + eventID
      break
    case 'leaderboard':
      url = '/app/leaderboard/' + eventID
      break
    case 'homepage':
      url = '/app/homepage/' + eventID
      break
    case 'opt_in_p2p':
      url = '/app/opt_in_p2p/' + eventID
      break
    case 'opt_out_p2p':
      url = '/app/opt_out_p2p/' + eventID
      break
    default:
      url = ''
    }
    return url
  }

  logout() {
    localStorage.removeItem('credentials')
    localStorage.removeItem('users')
    localStorage.removeItem('messages')
    localStorage.removeItem('events')
    localStorage.removeItem('sessions')
    localStorage.removeItem('sponsors')
    localStorage.removeItem('leaderboard')
    localStorage.removeItem('homepage')
    localStorage.removeItem('logged_in_user')
    localStorage.removeItem('agenda_filters')
    localStorage.removeItem('user_filters')
    this.setState({ credentials: null })
    window.location.href = '/'
    window.trackPage('LogoutScreen') // make sure this works/is what we want
  }

  getDefaultStartPageKey(currentUser) {
    var defaultStartPage = _.find(currentUser.app_navigations, (nav) => {
      return nav.is_default
    })
    return defaultStartPage?.key || currentUser.app_navigations[0]
  }

  // Post Requests

  postUserBusinessCard(options) {
    let { data, callback } = options
    data.created_by = this.state.currentUser.id
    data = { user_business_card: data }
    this.api.post('/app/user_business_cards', data).then((response) => {
      callback(response.data, response.status)
    }).catch((error) => {
      callback(error.response.data.errors.user_business_card[0], error.response.status)
    })
  }

  postComment(options) {
    let { data, callback } = options
    data.created_by = this.state.currentUser.id
    data = { comment: data }
    this.api.post('/app/comments', data).then((response) => {
      let sessions = this.state.sessions.sessions
      let sessionID = data.comment.event_session_id
      let session = sessions.find((session) => {
        return session.id === sessionID
      })
      session.comments.unshift(response.data.comment)
      this.setState({
        sessions: { sessions: sessions }
      })
      localStorage.setItem('sessions', JSON.stringify({ sessions: sessions }))
      callback(response.data.comment)
    }).catch((error) => {
      if(error.response.status === 401) {
        this.logout()
      }
    })
  }

  postMessage(options) {
    let { data, callback } = options
    data = { message: data }
    this.api.post('/app/messages', data).then((response) => {
      let messages = this.state.messages
      // this response is wonky
      var msg_resp = response.data.message
      msg_resp.message = msg_resp.body
      messages.messages.push(msg_resp)
      this.setState({messages: messages})
      localStorage.setItem('messages', JSON.stringify({ messages: messages.messages }))
      callback(response.data.message)
    }).catch((error) => {
      if(error.response.status === 403){
        this.flashMessage({ message: 'This user\'s messages have been disabled.', type: 'warning' })
      } else if (error.response.status === 401) {
        this.logout()
      }
    })
  }

  // PUT /app/mark_messages_read/:other_user_id
  postMarkMessagesRead(options) {
    var { data, callback } = options
    const url = `/app/mark_messages_read/${ data.other_user_id }`
    this.api.put(url).then((response) => {
      let messages = this.state.messages
      let newMessages = messages.messages.map( (message) => {
        if (message.sender_user_id === data.other_user_id) {
          return Object.assign({}, message, { has_been_read: true })
        } else {
          return message
        }
      })

      messages.messages = newMessages
      this.setState({ messages: messages })
      localStorage.setItem('messages', JSON.stringify({ messages: messages.messages }))
      callback(response)
    })
  }

  postNote(options) {
    let { data, callback } = options
    data = { event_note: data }
    this.api.post('/app/event_notes', data).then((response) => {
      if(response.data.event_note.event_user_id) {
        const usersObj = this.state.users
        const eventUsers = usersObj.event_users
        const eventUserID = response.data.event_note.event_user_id
        const eventUser = eventUsers.find((eventUser) => {
          return eventUser.id === eventUserID
        })
        eventUser.note_on_user = response.data.event_note.body
        this.setState({
          usersObj
        })
        localStorage.setItem('users', JSON.stringify(this.state.users))
      }
      if(response.data.event_note.sponsor_id) {
        const sponsorObj = this.state.sponsors
        const sponsors = sponsorObj.sponsors
        const sponsorId = response.data.event_note.sponsor_id
        const sponsor = sponsors.find((sponsor) => {
          return sponsor.id === sponsorId
        })
        sponsor.note_on_sponsor = response.data.event_note.body
        this.setState({
          sponsorObj
        })
        localStorage.setItem('sponsors', JSON.stringify(this.state.sponsors))
      }
      if(response.data.event_note.event_session_id) {
        const sessionObj = this.state.sessions
        const sessions = sessionObj.sessions
        const sessionId = response.data.event_note.event_session_id
        const session = sessions.find((session) => {
          return session.id === sessionId
        })
        session.note_on_session = response.data.event_note.body
        this.setState({
          sessionObj
        })
        localStorage.setItem('sessions', JSON.stringify(this.state.sessions))
      }
      callback(response.data)
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
    })
  }

  postSessionEvaluation(options){
    const data = options.data
    this.api.post('/app/event_session_surveys', data).then((response) => {
      const sessionObj = this.state.sessions
      const sessions = sessionObj.sessions
      const sessionId = response.data.event_session_survey.event_session_id
      const session = sessions.find((session) => {
        return session.id === sessionId
      })
      session.evaluation_completed = true
      this.setState({
        sessionObj
      })
      localStorage.setItem('sessions', JSON.stringify(this.state.sessions))
      options.callback(response)
    }).catch((error) => {
      window.Rollbar.error('error:' + error)
      if (error.response.status === 401) {
        this.logout()
      }
      if (error.response.status === 422 && error.response.data.errors.not_enough_speakers) {
        this.flashMessage({ message: error.response.data.errors.not_enough_speakers[0], type: 'warning' })
      }
      if (error.response.status === 422 && error.response.data.errors.session_has_not_started) {
        this.flashMessage({ message: error.response.data.errors.session_has_not_started[0], type: 'error' })
      }
    })
  }

  postOneOnOneSessionEvaluation(options){
    const data = options.data
    const eventOneOnOneMeetingId = data.event_one_on_one_meeting_survey.event_one_on_one_meeting_id
    this.api.post(`/app/event_one_on_one_meeting_survey/${eventOneOnOneMeetingId}`, data).then((response) => {
      const sessionObj = this.state.sessions
      const sessions = sessionObj.sessions
      var session = _.find(sessions, { id: options.session.id })
      var one_on_one_meeting = _.find(session.one_on_one_meetings, { id: eventOneOnOneMeetingId })
      one_on_one_meeting.evaluation_completed = true
      this.setState({ sessionObj })
      localStorage.setItem('sessions', JSON.stringify(this.state.sessions))
      options.callback(response)
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
      if (error.response.status === 422) {
        this.flashMessage({ message: error.response.data.errors.timeblock_has_not_started[0], type: 'error' })
      }
    })
  }

  postSessionRegistration(options){
    const data = options.data
    this.api.post(`/app/toggle_registration/${data.id}`).then((response) => {
      const sessionObj = this.state.sessions
      const sessions = sessionObj.sessions
      const sessionId = options.data.id
      const session = sessions.find((session) => {
        return session.id === sessionId
      })
      session.registration_status = response.data.event_session_registration.registration_status
      this.setState({
        sessions: sessionObj
      })
      localStorage.setItem('sessions', JSON.stringify(this.state.sessions))
      options.callback(response)
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
    })
  }

  postPhoto(options){
    const data = options.data
    this.api.post('users/upload', data).then((response) => {
      options.callback(response)
    })
  }

  postResetPassword(options){
    const data = options.data
    this.api.put('/users/password/reset', data).then((response) => {
      let credentials = this.state.credentials
      let newAuth = response.data.authentication_token
      credentials.auth_token = newAuth
      credentials.temp_password = null
      this.notificationSystem.addNotification({
        message: getLabel('password_reset_success'),
        level: 'success',
        position: 'tc'
      })
      localStorage.setItem('credentials', JSON.stringify(credentials))
      this.setState({
        credentials: credentials
      }, this.initializeApi)
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
    })
  }

  postChangePassword(options){
    const data = options.data
    this.api.put('/users/password/change', data).then((response) => {
      let credentials = this.state.credentials
      let newAuth = response.data.authentication_token
      credentials.auth_token = newAuth
      localStorage.setItem('credentials', JSON.stringify(credentials))
      this.setState({
        credentials: credentials
      }, this.initializeApi)
      this.flashMessage({
        message: getLabel('password_reset_success')
      })
      if(response.status === 204){
        setTimeout(function() {
          window.location.href = '/settings_and_support'
        }, 2000)
      }
      options.callback()
    })
      .catch((error) => {
        if(error.response.status === 422){
          this.flashMessage({ message: error.response.data.errors.password[0], type: 'warning' })
        } else if (error.response.status === 401) {
          this.logout()
        }
      })
  }

  postProfileEdits(options){
    const data = options.data
    const user = this.state.currentUser
    const eventUser = this.state.users.event_users.find((eventUser) => {
      return eventUser.user_id === user.user_id
    })
    this.api.put('/users/profile', data).then((response) => {
      this.notificationSystem.addNotification({
        message: getLabel('changes_saved'),
        level: 'success',
        position: 'tc'
      })
      eventUser.first_name = user.first_name = data.user.first_name
      eventUser.last_name = user.last_name = data.user.last_name
      eventUser.title = user.title = data.user.title
      eventUser.organization_name = user.organization_name = data.user.organization_name
      eventUser.phone = user.phone = data.user.phone
      eventUser.address = user.address = data.user.address
      eventUser.city = user.city = data.user.city
      eventUser.state = user.state = data.user.state
      eventUser.postal_code = user.postal_code = data.user.postal_code
      eventUser.bio = user.bio = data.user.bio
      eventUser.dietary_restrictions = user.dietary_restrictions = data.user.dietary_restrictions
      this.setState({
        currentUser: user
      })
      options.callback(response)
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
    })
  }

  postUserFlags(options){
    const data = options.data
    const user = this.state.currentUser
    const eventUser = this.state.users.event_users.find((eventUser) => {
      return eventUser.user_id === user.user_id
    })
    this.api.put('/users/profile', data).then(() => {
      this.notificationSystem.addNotification({
        message: getLabel('changes_saved'),
        level: 'success',
        position: 'tc'
      })
      let usersFromLocalStorage = JSON.parse(localStorage.getItem('users'))
      let localUser = _.find(usersFromLocalStorage.event_users, {user_id: parseInt(eventUser.user_id, 10)})
      localUser.block_messages = localUser.block_messages ? false : true
      localStorage.setItem('users', JSON.stringify(usersFromLocalStorage))
      this.setState(
        { currentUser: user },
      )
    }).catch((error) => {
      if (error.response.status === 401) {
        this.logout()
      }
    })
  }


  // types: 'success', 'error', 'warning', 'info'
  flashMessage(options) {
    let { message, type } = options
    message = message ? message : ''
    type = type ? type : 'success'
    this.notificationSystem.addNotification({
      message: message,
      level: type,
      position: 'tc',
      dismissible: 'click'
    })
  }

  postResource(options) {
    let { key } = options
    switch (key) {
    case 'comment':
      this.postComment(options)
      break
    case 'message':
      this.postMessage(options)
      break
    case 'mark_messages_read':
      this.postMarkMessagesRead(options)
      break
    case 'note':
      this.postNote(options)
      break
    case 'session_evaluation':
      this.postSessionEvaluation(options)
      break
    case 'one_on_one_session_evaluation':
      this.postOneOnOneSessionEvaluation(options)
      break
    case 'session_registration':
      this.postSessionRegistration(options)
      break
    case 'post_photo':
      this.postPhoto(options)
      break
    case 'profile_edits':
      this.postProfileEdits(options)
      break
    case 'update_user_flags':
      this.postUserFlags(options)
      break
    case 'reset_password':
      this.postResetPassword(options)
      break
    case 'change_password':
      this.postChangePassword(options)
      break
    case 'user_business_card':
      this.postUserBusinessCard(options)
      break
    default:
      console.log('Eror: no post method for ' + key)
    }
  }

  render() {
    let { credentials,
      currentUser,
      users,
      messages,
      events,
      sessions,
      sponsors,
      leaderboard,
      homepage,
    } = this.state

    // rougher estimates
    let percentLoaded = 0
    percentLoaded += users ?       30 : 0 // 30%
    percentLoaded += messages ?    10 : 0 // 40%
    percentLoaded += events ?      10 : 0 // 50%
    percentLoaded += sessions ?    15 : 0 // 65%
    percentLoaded += sponsors ?    15 : 0 // 80%
    percentLoaded += leaderboard ? 10 : 0 // 90%
    percentLoaded += homepage ?    10 : 0 // 100%

    const hasSetPassword = credentials && !credentials.temp_password

    const readyToLoad = hasSetPassword && percentLoaded === 100

    let component = (<div></div>)
    if (!credentials) {
      // Login
      component = (
        <div>
          <div className='splash-screen'>
            <div className='splash-logo'>
              <img src='/gartner_c_level_communities_white.svg' alt='' />
            </div>
            <div className="spinner-container">
              <div className="fa fa-spinner fa-spin"></div>
            </div>
          </div>
          <LoginForm flashMessage ={ this.flashMessage } />
        </div>
      )
    } else if (!hasSetPassword) {
      // ResetPassword
      component = (
        <ResetPasswordForm setPermanentPassword={ this.postResetPassword } />
      )
    } 
    // comment out for forwarding info to myevanta
    else if (readyToLoad) {
      // Layout
      component = (
        <Layout logout={ this.logout }
          getResource={ this.getResource }
          postResource={ this.postResource }
          refreshData={ this.refreshData }
          changeEvent={ this.changeEvent }
          loadSurveyFromEmail={ this.loadSurveyFromEmail }
          flashMessage={ this.flashMessage }
          users={ users }
          messages={ messages }
          currentUser={ currentUser }
          events={ events }
          sessions={ sessions }
          sponsors={ sponsors }
          leaderboard={ leaderboard }
          homepage={ homepage } />
      )
    } 
    else {
      component = (
        <LoadingPage percentLoaded={ percentLoaded } />
      )
    }

    return (
      <div>
        <SpoofWarning
          currentUser={ currentUser }
          credentials={ credentials }
          users={ users } />
        { component }
        <NotificationSystem
          ref={ this.notificationSystemRef }
          style={ flashMessageStyles } />
      </div>
    )
  }
}
